// The build card: the card of one catalogue item (ui-map-interface.md §8
// "The build card"; mockup round 084 cards 13 to 16, round 087 card 02,
// R1826). One spec per kind, each a pure function of the game state and the
// id it explains, written in the anatomy of the card kit (`ui/cards/`):
// the head (the icon, the name in the hero ink, the kind and the level, the
// state of the press as a chip), the body (the sentence, the effect block
// NOW → AFTER from the server's preview, the facts), the note (the reason
// of a lock), and the action foot with the verb press of the row. A card
// re-labels figures the hello catalog and the empire frame already carry
// and computes no rule. The press is the row's own press
// (`buildingPress` and its siblings in `menus.ts`), so the card and the
// row never offer two different things.
//
// `ui/overlay.ts` stands the card in the context slot on a desktop and as
// a page of the menu sheet on a telephone. The three readers the cards
// share with the menus stand here as well: the rounds a hold stores, who
// fires a round and how many one press orders, and what a captain does now.

import { Building, CaptainPhase, JobKind, LevelEffectKind, SeaObjectKind, ShipClass } from '../../gen/proto/aquaphobia';
import type {
  BuildingLevel, BuildingPreview, Captain, CaptainRules, LevelEffect, Resources, ShipSpec, TechSpec, TorpedoSpec,
} from '../../gen/proto/aquaphobia';
import { type GameState, RES_ORDER, costText, dur, num } from '../../state';
import { withResIcons } from '../resicon';
import { overlay as S, card as CS } from '../../gen/strings';
import { magazineFor, pxFig, shotDamageText, shotText, speedPxMin } from '../format';
import { esc } from '../esc';
import { gradeOdds } from '../fit';
import { askBackstories, backstoryOf } from '../backstory';
import { termsHtml } from '../glossary';
import { type Action, type CardSpec, type EffectRow, type Fact, effectHtml, factsHtml } from '../cards/card';
import { type CompareRow, compareHtml } from '../cards/compare';
import {
  attrTrainFigures, attrValueText, captainEffectText, pctNum, practiceDeclineText, practiceStopWord,
} from './captaincard';
import {
  attrName, capFleetButton, captainActions, phaseWord, trainGate, trainingJob, trainPickGate,
} from './caprow';
import {
  diveName, effectParts, effectRow, fitStrip, moduleGradeWord, moduleWithGrade, outfitJob, unlockFacts,
} from './markup';
import { ATTR_SHORT, ROLE_MANY, ROLE_ONE } from './tables';
import { type RowPress, pressAction, pressChip, rowIcon, shortNote } from './buildrow';
import { buildingIcon, techIcon } from '../buildicons';
import { shipMarkSvg } from '../cards/marks';
import type { SceneLook } from '../../render/scene-look';
import { buildingPress, shipPress, techPress, torpedoPress } from './menus';
import { levelPickHtml } from './levelpick';
import { type CommissionFleet, commissionSpec } from './commissions';
import { depotHeld, seaObjectPress } from './depot';
import { clamp01 } from '../../render/math';
import { groupThousands } from '../../locale/group';

/** What the build card explains: one catalog kind and its id. */
export type InfoKind = 'building' | 'ship' | 'tech' | 'torpedo' | 'module' | 'captain'
  // The commission card of FLEET · COMMISSIONS (v2 phase 5, R2645).
  | 'commission'
  // The card of one kind of sea object of BUILD · DEPOT (R2708).
  | 'seaobject';

export interface InfoTarget { kind: InfoKind; id: number }

/** What a card reads beside the state. */
export interface InfoCtx {
  /** The captain card asks for the owner's backstory (R1246) and calls
   *  this when it lands, so the card draws it then. */
  onBackstory: () => void;
  /** The own fleets at sea, which name a captain's fleet; the BUILD press
   *  of a hull card counts them against the class cap (R2628). */
  fleets: readonly { id: number; name: string; cls: ShipClass }[];
  /** The own fleets at sea with their captain and place, which the fit
   *  list of the commission card reads (R2645). None lists no fleet. */
  sea?: readonly CommissionFleet[];
  /** The backstory of the captain card stands unfolded (MORE). */
  storyOpen: boolean;
  /** The TRAIN pick of the captain card stands open (R2087). */
  trainPick?: boolean;
  /** The hull that COMPARE marked, 0 for none (084 card 17, R1829). */
  compareFrom: number;
  /** The server's answer for the levels past the next of a building row,
   *  or null while none holds (084 card 17, R1839). */
  levelPreview(bl: BuildingLevel | undefined): BuildingPreview | null;
  /** The level the picker shows; 0 or a level the answer lacks shows the
   *  first level past the next. */
  levelPick: number;
  /** The reader's look, whose ship icon set draws the hull mark of a hull
   *  card, as the shipyard row draws it (R1872). No look is the AUTO set. */
  look?: SceneLook;
  /** The arm gate of the overlay (R696): the captain card draws an armed
   *  RELEASE as RELEASE? (R2069). None reads as never armed. */
  armed?: (key: string) => boolean;
}

/** The spec of one target, or null when the catalog does not hold it. The
 *  overlay sets the size and the ✕. */
export function infoSpec(s: GameState, t: InfoTarget, ctx: InfoCtx): CardSpec | null {
  switch (t.kind) {
    case 'building': return buildingSpec(s, t.id, ctx);
    case 'ship': return shipSpec(s, t.id, ctx.compareFrom, ctx.look, ctx.fleets);
    case 'torpedo': return torpedoSpec(s, t.id);
    case 'module': return moduleSpec(s, t.id);
    case 'tech': return techSpec(s, t.id);
    case 'captain': return captainSpec(s, t.id, ctx);
    case 'commission': return commissionSpec(s, t.id, ctx.sea ?? []);
    case 'seaobject': return seaObjectSpec(s, t.id);
  }
}

// --- the parts every card shares --------------------------------------------

/** The sentence of what the item does, in the small step. */
const desc = (text: string): string => (text ? `<p class="ck-desc">${esc(text)}</p>` : '');

/** A section head in the body, then its rows. */
const sect = (head: string, html: string): string =>
  (html ? `<div class="ck-sh ck-cap">${esc(head)}</div>${html}` : '');

/** One fact whose value is plain text. */
const fact = (key: string, val: string): Fact => ({ key, val: esc(val) });

/** The note of a press: the reason of a lock, as escaped markup, and the
 *  shortfall of a press short of stores with its wait (R3258). */
function pressNote(why: string, press: RowPress, more: string[] = []): CardSpec['note'] {
  const lines = [why, shortNote(press), ...more].filter((l) => l !== '');
  return lines.length ? { lines } : undefined;
}

/**
 * The `and takes` fact (the time THIS empire pays) and the three facts
 * under it (§8, R688): the catalog's list time, the rate this empire
 * earned, and what earned it. The three drop out at 1x, because nothing was
 * earned there. The rate is a figure the empire frame carries, so
 * `workSec / rate` re-labels two server figures and mirrors no table.
 */
function takesFacts(s: GameState, kind: JobKind, workSec: number): Fact[] {
  const out = [fact(S.info_and_takes, dur(s.buildSec(kind, workSec)))];
  const rate = s.queueRate(kind);
  if (rate === 1) return out;
  const sources = (s.empire?.queueRateSources ?? [])
    .filter((src) => src.kind === kind)
    .map((src) => (src.level > 0 ? S.info_source_level({ name: src.name, level: src.level }) : src.name));
  out.push(fact(S.info_list_time, dur(workSec)), fact(S.info_your_rate, S.info_rate_val({ rate: rate.toFixed(1) })));
  if (sources.length > 0) out.push(fact(S.info_from, sources.join(', ')));
  return out;
}

// --- an installation (084 card 13) ------------------------------------------

function buildingSpec(s: GameState, id: number, ctx: InfoCtx): CardSpec | null {
  const b = s.building(id);
  if (!b) return null;
  const bl = s.empire?.buildings.find((x) => x.building === id);
  const level = bl?.level ?? 0;
  const maxed = bl?.maxed ?? false;
  const { press, why } = buildingPress(s, b);
  // The level the BUILD press buys sits above the queued upgrades, and
  // the server prices and previews that level (R1081).
  const next = level + (bl?.queued ?? 0) + 1;
  // What this level is worth, and what the level of the press makes it.
  // A figure that takes no step is a fact.
  const effect: EffectRow[] = [];
  const facts: Fact[] = [];
  for (const e of bl?.effects ?? []) {
    const row = effectRow(e, maxed);
    if (row) { effect.push(row); continue; }
    const p = effectParts(e, true);
    if (p) facts.push({ key: p.label, val: p.value });
  }
  if (!maxed) {
    facts.push({ key: S.info_next_level_costs({ level: next }), val: costText(bl?.nextCost) });
    facts.push(...takesFacts(s, JobKind.JOB_KIND_BUILDING, bl?.nextTimeSec ?? 0));
  }
  if (b.tech !== 0) facts.push(fact(S.info_needs, s.tech(b.tech)?.name ?? S.word_a_doctrine));
  // The next level needs a Command Centre level (numbers.md §2, R2862).
  // The server states the gate on the row, so the card computes none;
  // a gate of Command Centre 1 is no news and shows no fact.
  const needsBase = bl?.nextNeedsBase ?? 0;
  if (!maxed && needsBase > 1) {
    facts.push(fact(S.info_needs, S.info_capped_base({ level: needsBase })));
  }
  if (bl?.queued) facts.push(fact(S.info_in_the_queue, S.info_queued_more({ n: bl.queued })));
  // The level picker (084 card 17, R1839): once the server's answer for
  // the levels past the next holds, the grid NOW · NEXT · AT k stands in
  // place of the effect block, because it carries the same two ends.
  const preview = ctx.levelPreview(bl);
  const levels = bl && preview ? levelPickHtml(s, bl, preview, ctx.levelPick) : '';
  return {
    size: 'card', mark: buildingIcon(id), title: b.name, ink: 'catalogue',
    kind: maxed ? CS.kind_building_max({ level }) : CS.kind_building({ level, next }),
    status: pressChip(press.state),
    // The unlock ladder rides the catalog, because it is static (R1081).
    body: desc(b.effect) + (levels || effectHtml(effect)) + factsHtml(facts)
      + sect(CS.sect_opens, factsHtml(unlockFacts(s, b.unlocks, level))),
    note: pressNote(why, press),
    actions: [pressAction(press)],
  };
}

// --- a hull class (084 card 14) ---------------------------------------------

/**
 * A figure of the hull class and what the fit of the print makes of it
 * (R1840): the class figure alone, or `class → fitted` with the gain in
 * the good ink. Both figures are the server's (the catalog and
 * `Blueprint.fitted`); the gain is their difference, and the card applies
 * no module itself (R701). `now` writes the class figure bare, `next` the
 * fitted one with its unit, and `gain` the difference.
 */
function gainFig(base: number, fitted: number | undefined, now: (v: number) => string,
  next: (v: number) => string = now, gain: (v: number) => string = now): string {
  if (fitted === undefined || fitted === base) return esc(next(base));
  const d = fitted - base;
  return esc(S.eff_step({ now: now(base), next: next(fitted) }))
    + ` <small class="gain">${d < 0 ? '−' : '+'}${esc(gain(Math.abs(d)))}</small>`;
}

function shipSpec(s: GameState, id: number, compareFrom: number, look?: SceneLook,
  sea: readonly { cls: ShipClass }[] = []): CardSpec | null {
  const sp = s.ship(id);
  if (!sp) return null;
  const { press, why, print } = shipPress(s, sp, sea);
  // What the fit of the print makes of the hull (R1840): the server's
  // figures, written beside the class figure with the gain.
  const fit = print?.fitted;
  // The figures of the class in a two-column grid: every range in px,
  // the speed in px per wall minute on the map clock (R3270). A hull with
  // a dead zone fires into a band (R407).
  const pxMin = (uh: number) => speedPxMin(uh, false, s.tacticalScale);
  const grid: Fact[] = [
    fact(CS.v_hull, num(sp.hull)),
    { key: CS.v_shield, val: gainFig(sp.shield, fit?.shield, num) },
    {
      key: CS.f_speed,
      val: gainFig(pxMin(sp.speedUh), fit ? pxMin(fit.speedUh) : undefined, num, (n) => S.info_speed_val({ n: num(n) }), num),
    },
    fact(CS.f_weight, String(sp.weight)),
    { key: CS.f_sonar, val: gainFig(sp.sonar, fit?.sonar, num, pxFig, num) },
    fact(CS.f_reach, sp.dps ? pxFig(sp.range) : CS.none),
    fact(CS.f_dead_zone, sp.minRange > 0 ? pxFig(sp.minRange) : CS.none),
  ];
  // A fit that cuts the noise states it, in the grid (R1840).
  if (fit && fit.noise !== sp.noise) {
    grid.push({ key: CS.f_noise, val: gainFig(sp.noise, fit.noise, (v) => v.toFixed(2)) });
  }
  const facts: Fact[] = [];
  // The fit of the print the press builds to, as a strip of codes.
  if (print) {
    const strip = fitStrip(s.catalog, { weapType: print.weapType, shieldType: print.shieldType, fit: print.modules });
    if (strip) facts.push({ key: CS.f_fit, val: strip });
  }
  // The shot (R1135, R1136): one shot's damage and the fire period, both
  // off the catalog. An unarmed hull states none.
  // A print's Weapon modules move the damage of one shot: the fitted shot
  // is the server's figure, with its gain (R1840).
  const shot = shotText(fit ? { firePeriodSec: sp.firePeriodSec, shotDamageX10: fit.shotDamageX10 } : sp);
  if (shot && fit && fit.shotDamageX10 !== sp.shotDamageX10) {
    const d = fit.shotDamageX10 - sp.shotDamageX10;
    facts.push({
      key: S.info_shot,
      val: `${esc(shot)} <small class="gain">${d < 0 ? '−' : '+'}${esc(shotDamageText(Math.abs(d)))}</small>`,
    });
  } else if (shot) facts.push(fact(S.info_shot, shot));
  // The doctrine rows, derived from the catalog's matchups: the roles this
  // hull's guns beat, and the roles it fights into at a loss.
  const role = ROLE_ONE[sp.role];
  if (role) {
    facts.push(fact(S.info_role, role));
    const web = s.catalog?.roleBonuses ?? [];
    const targets = (better: boolean) => web
      .filter((r) => r.attacker === sp.role && (better ? r.mul > 1 : r.mul < 1))
      .map((r) => ROLE_MANY[r.target] ?? '').filter((w) => w !== '').join(' · ');
    const strong = targets(true);
    const weak = targets(false);
    if (strong) facts.push(fact(S.info_strong_vs, strong));
    if (weak) facts.push(fact(S.info_weak_vs, weak));
  }
  if (sp.cargo) facts.push(fact(S.info_hold, S.info_per_hull({ n: sp.cargo })));
  facts.push(...anchorFacts(s, sp));
  facts.push({ key: S.info_costs, val: costText(print?.cost ?? sp.cost) });
  facts.push(...takesFacts(s, JobKind.JOB_KIND_SHIP, sp.timeSec));
  if (sp.tech !== 0) facts.push(fact(S.info_needs, s.tech(sp.tech)?.name ?? S.word_certification));
  // The Shipyard level this hull waits for (R684).
  if (sp.wharf > 0) facts.push(fact(S.info_needs, S.info_shipyard_level({ level: sp.wharf })));
  // What one more of this hull does to its weight bucket (R1081): the
  // weight, the load and the cap are all server figures.
  const e = s.empire;
  const bucket = sp.support ? S.bucket_support : S.bucket_combat;
  const effect: EffectRow[] = [];
  if (e) {
    const used = sp.support ? e.supportWeight : e.combatWeight;
    const cap = sp.support ? e.supportCap : e.combatCap;
    effect.push({
      key: S.info_bucket_use({ bucket }),
      now: esc(S.info_bucket_of({ used, cap })),
      after: esc(S.info_bucket_of({ used: used + sp.weight, cap })),
      delta: `+${sp.weight}`,
      tone: used + sp.weight > cap ? 'bad' : undefined,
    });
  }
  // A print names its modules with the rolled effect of each (R1194).
  const printNote = print
    ? CS.note_print({
      name: esc(print.name),
      modules: print.modules.map((m) => CS.note_module({
        name: moduleWithGrade(m), pct: (m.effectPct / 10).toFixed(1),
      })).join(' · '),
    })
    : esc(CS.note_base_design);
  return {
    size: 'card', mark: shipMarkSvg(sp.id, look), title: sp.name, ink: 'catalogue',
    kindHtml: termsHtml(S.info_ship_sub({ bucket, weight: sp.weight })),
    status: print ? { text: CS.st_print({ name: print.name }), tone: 'good' } : { text: CS.st_base_design, tone: 'kind' },
    body: factsHtml(grid, 'ck-f2') + effectHtml(effect) + factsHtml(facts),
    note: pressNote(why, press, [printNote]),
    // COMPARE marks this hull, and a press on the name of a second hull
    // opens the two in one card (R1829). It is lit while it waits.
    actions: [pressAction(press), {
      act: 'compare', id: sp.id, face: 'quiet', label: CS.compare, on: compareFrom === sp.id,
      title: CS.compare_title,
    }],
  };
}

// --- two hulls side by side (084 card 17, R1829) -----------------------------

/** Does cost `a` ask no more than cost `b` of every resource, and less of
 *  one? Only then is it the better cost; two costs that trade one resource
 *  for another have no better. */
function cheaper(a: Resources | undefined, b: Resources | undefined): boolean {
  const x = (r: Resources | undefined, k: keyof Resources) => r?.[k] ?? 0;
  return RES_ORDER.every((k) => x(a, k) <= x(b, k)) && RES_ORDER.some((k) => x(a, k) < x(b, k));
}

/**
 * The rows of the compare card of two hull classes: the same figures in two
 * columns, and the column of the better figure of each row. A higher hull,
 * shield, speed, sonar and reach is better, a smaller dead zone is better,
 * and a cost is better only when it asks no more of every resource. The
 * weight and the role have no better. The judgement is a reading of two
 * catalog rows; it computes no rule.
 */
export function compareRows(s: GameState, a: ShipSpec, b: ShipSpec): CompareRow[] {
  const more = (x: number, y: number) => (x > y ? 0 : y > x ? 1 : -1);
  const reach = (sp: ShipSpec) => (sp.dps ? sp.range : 0);
  const cost = (sp: ShipSpec) => shipPress(s, sp).press.cost;
  const ca = cost(a);
  const cb = cost(b);
  const role = (sp: ShipSpec) => ROLE_ONE[sp.role] ?? sp.role;
  return [
    { key: CS.v_hull, vals: [num(a.hull), num(b.hull)], best: more(a.hull, b.hull) },
    { key: CS.v_shield, vals: [num(a.shield), num(b.shield)], best: more(a.shield, b.shield) },
    {
      key: CS.f_speed, vals: [a, b].map((x) => esc(S.info_speed_val({ n: num(speedPxMin(x.speedUh, false, s.tacticalScale)) }))),
      best: more(a.speedUh, b.speedUh),
    },
    { key: CS.f_sonar, vals: [esc(pxFig(a.sonar)), esc(pxFig(b.sonar))], best: more(a.sonar, b.sonar) },
    {
      key: CS.f_reach, vals: [a.dps ? esc(pxFig(a.range)) : CS.none, b.dps ? esc(pxFig(b.range)) : CS.none],
      best: more(reach(a), reach(b)),
    },
    {
      key: CS.f_dead_zone,
      vals: [a.minRange > 0 ? esc(pxFig(a.minRange)) : CS.none, b.minRange > 0 ? esc(pxFig(b.minRange)) : CS.none],
      best: more(b.minRange, a.minRange),
    },
    { key: S.info_role, vals: [esc(role(a)), esc(role(b))], best: -1 },
    { key: CS.f_weight, vals: [String(a.weight), String(b.weight)], best: -1 },
    { key: S.info_costs, vals: [costText(ca), costText(cb)], best: cheaper(ca, cb) ? 0 : cheaper(cb, ca) ? 1 : -1 },
  ];
}

/**
 * The compare card of two hull classes (084 card 17): one card with two
 * columns, the better figure of each row marked with a filled triangle, a
 * shape and not a colour. It is for the catalogue only, never for two
 * fleets on the map (R1307). The two BUILD presses are the presses of the
 * two rows, the first in the dark face.
 */
export function compareSpec(s: GameState, aId: number, bId: number): CardSpec | null {
  const a = s.ship(aId);
  const b = s.ship(bId);
  if (!a || !b) return null;
  const build = (sp: ShipSpec, face: 'dark' | 'quiet'): Action => ({
    ...pressAction(shipPress(s, sp).press), face, costHtml: undefined,
    label: CS.build_named({ name: sp.name.toUpperCase() }),
  });
  return {
    size: 'card', mark: rowIcon('hull'), title: `${a.name} · ${b.name}`, ink: 'catalogue',
    kind: CS.compare_kind, status: { text: CS.compare, tone: 'on' },
    body: compareHtml([a.name.toUpperCase(), b.name.toUpperCase()], compareRows(s, a, b)),
    note: { lines: [esc(CS.compare_note)] },
    actions: [build(a, 'dark'), build(b, 'quiet')],
  };
}

/**
 * What the anchor buys and what the Tender does (numbers.md §3, R1495),
 * every figure a catalog figure. The repair and the restock run on the map
 * clock, so the facts state the world time of that interval (R591). The
 * aura is a damage rate in combat seconds.
 */
function anchorFacts(s: GameState, sp: ShipSpec): Fact[] {
  const r = s.rules;
  const tender = sp.id === ShipClass.SHIP_CLASS_TENDER;
  const out: Fact[] = [];
  if (sp.anchorRange > 0) out.push(fact(S.info_anchored_range, S.info_px({ n: sp.anchorRange })));
  // Only a fixture pays the noise price: the Sentinel, the Tender and the
  // Beacon (R661, R712, R2858). A plain anchor pays none.
  const beacon = sp.id === ShipClass.SHIP_CLASS_BEACON;
  if ((sp.anchorRange > 0 || tender || beacon) && r.anchorNoiseMul > 0) {
    out.push(fact(S.info_anchored_noise, S.info_noise_mul_val({ mul: pctNum(r.anchorNoiseMul) })));
  }
  const rings = s.catalog?.anchorRings ?? 0;
  if (beacon && rings > 0) {
    out.push(fact(S.info_anchored, S.info_beacon_beam({ n: rings })));
  }
  if (!tender) return out;
  if (r.tenderRepairRangePx > 0) {
    out.push(fact(S.info_repairs, S.info_repair_val({
      range: num(r.tenderRepairRangePx), pct: pctNum(r.tenderRepairPerMapMinute * 100),
      every: dur(s.mapToWorldSec(60)),
    })));
  }
  if (r.tenderAuraPx > 0) {
    out.push(fact(S.info_anchored, S.info_tender_aura({
      range: num(r.tenderAuraPx), pct: pctNum(r.tenderShieldPerCombatSec * 100),
    })));
  }
  if (r.tenderRestockEverySec > 0) {
    out.push(fact(S.info_anchored, S.info_tender_restock({ every: dur(s.mapToWorldSec(r.tenderRestockEverySec)) })));
  }
  // The stow count is the one the fleet card's rounds bar reads: the hold
  // over one round's cargo (R1448). A hull that stows two kinds states each.
  for (const kind of sp.stows) {
    const tp = s.torpedo(kind);
    const n = magazineFor(sp, tp);
    if (tp && n > 0) out.push(fact(S.info_stows, S.info_stows_val({ n, round: tp.name })));
  }
  return out;
}

// --- a round ----------------------------------------------------------------

/** The rounds of one kind the hold's stores hold. The BUILD row, the
 * pens' tube picker, the base panel and the round's card all read it. */
export function roundsHeld(s: GameState, kind: number): number {
  return s.empire?.torpedoes.find((t) => t.kind === kind)?.count ?? 0;
}

/** The rack of each base round kind, the Torpedo Battery's figure (R86):
 *  the BUILD · TORPEDO head and the round's card read it. */
export function baseRack(e: { buildings: readonly BuildingLevel[] }): LevelEffect | undefined {
  return e.buildings.find((b) => b.building === Building.BUILDING_TORPEDOBANKS)
    ?.effects.find((x) => x.kind === LevelEffectKind.LEVEL_EFFECT_TORPEDO_STORAGE);
}

/**
 * Who fires a round: Torpedo Battery for a base round, and otherwise every
 * hull class whose catalog row allows the kind (R82). The client mirrors
 * no tube table of its own.
 */
function torpedoFirers(s: GameState, tp: TorpedoSpec): string {
  if (tp.fromBase) return S.word_torpedobanks;
  return (s.catalog?.ships ?? []).filter((sh) => sh.torpedoes.includes(tp.id))
    .map((sh) => sh.name).join(' / ') || '—';
}

/**
 * How many rounds one press of the row's button orders. A base round
 * goes one at a time, because a Torpedo Battery holds few; a ship-fired
 * round goes four, which is the smallest useful load.
 */
export function torpedoBatch(tp: TorpedoSpec): number { return tp.fromBase ? 1 : 4; }

function torpedoSpec(s: GameState, id: number): CardSpec | null {
  const tp = s.torpedo(id);
  if (!tp) return null;
  const { press, why } = torpedoPress(s, tp);
  // The figures are the catalog's own cells (R698).
  const facts: Fact[] = [
    fact(S.info_damage, num(tp.damage)),
    fact(S.info_splash, tp.splash ? S.info_px({ n: tp.splash }) : S.info_none),
  ];
  // Friendly fire only means something where there is a splash.
  if (tp.splash) facts.push(fact(S.info_friendly_fire, tp.friendlyFire ? S.info_ff_hits_own : S.info_ff_spares_own));
  facts.push(
    fact(S.info_speed, S.info_speed_val({ n: num(speedPxMin(tp.speedUh, false, s.tacticalScale)) })),
    fact(S.info_reach, S.info_px({ n: tp.range })),
  );
  // A base round costs no magazine space, because no hull can load one
  // (R699). The fact drops out rather than printing a zero.
  if (tp.cargo) facts.push(fact(S.info_magazine, S.info_magazine_val({ n: tp.cargo })));
  // One round's price and clock: the press prices the whole order, so the
  // fact names the unit it states.
  facts.push({ key: S.info_round_costs, val: costText(tp.cost) });
  facts.push(...takesFacts(s, JobKind.JOB_KIND_TORPEDO, tp.timeSec));
  if (tp.tech !== 0) facts.push(fact(S.info_needs, s.tech(tp.tech)?.name ?? S.word_doctrine));
  // What the order does to the stock (R1081): the rounds held now, and the
  // rounds held after the order the press lays down.
  const e = s.empire;
  const effect: EffectRow[] = [];
  if (e) {
    const held = roundsHeld(s, tp.id);
    const n = torpedoBatch(tp);
    effect.push({ key: S.info_rounds_held, now: groupThousands(held), after: groupThousands(held + n), delta: `+${n}` });
    // A base round fills a rack of its own, and the rack has a ceiling
    // (R86): the card states it before the server refuses the order.
    if (tp.fromBase) {
      const cap = baseRack(e);
      if (cap) facts.push(fact(S.info_rack_cap, groupThousands(Math.round(cap.now))));
    } else {
      // A ship round shares one rack with every ship-fired kind (R1788).
      facts.push(fact(S.info_ship_rack, S.info_bucket_of({
        used: groupThousands(e.shipRounds), cap: groupThousands(e.shipRoundCap),
      })));
    }
  }
  return {
    size: 'card', mark: rowIcon('round'), title: tp.name, ink: 'catalogue',
    kind: S.torpedo_use({ firers: torpedoFirers(s, tp), use: tp.use }),
    status: pressChip(press.state),
    body: desc(tp.effect) + effectHtml(effect) + factsHtml(facts),
    note: pressNote(esc(why), press),
    actions: [pressAction(press)],
  };
}

// --- a kind of sea object (BUILD · DEPOT) -------------------------------------

/**
 * The card of one kind of sea object (v2-the-deep.md §11.2, §11.3, §11.5;
 * R2708), the way the round card explains a round: the use of the kind in
 * one line, the figures the hello catalog carries for it (a figure the
 * kind does not have is 0 and drops out), the cargo of one item, the price
 * and the clock of one item, the gates, and the stock of the Depot now and
 * after the order. The press is the ORDER press of the row.
 */
function seaObjectSpec(s: GameState, id: number): CardSpec | null {
  const k = s.seaObject(id);
  if (!k) return null;
  const { press, why } = seaObjectPress(s, k);
  const px = (n: number) => S.info_px({ n: Math.round(n) });
  const facts: Fact[] = [];
  if (k.triggerPx > 0) facts.push(fact(S.info_so_trigger, px(k.triggerPx)));
  if (k.hearPx > 0) facts.push(fact(S.info_so_hears, S.info_so_hears_val({ n: Math.round(k.hearPx) })));
  if (k.damage > 0) facts.push(fact(S.info_damage, num(k.damage)));
  if (k.splashPx > 0) facts.push(fact(S.info_splash, px(k.splashPx)));
  // The Cheval is a disc; every other deployable with a length is a line.
  if (k.lengthPx > 0) {
    facts.push(fact(k.id === SeaObjectKind.SEA_OBJECT_KIND_CHEVAL ? S.info_so_radius : S.info_so_length, px(k.lengthPx)));
  }
  if (k.reachPx > 0) facts.push(fact(S.info_reach, px(k.reachPx)));
  if (k.sonarPx > 0) facts.push(fact(S.info_so_listens, px(k.sonarPx)));
  facts.push(fact(S.info_so_cargo, S.stow_weight({ n: k.cargo })));
  facts.push({ key: S.info_so_costs, val: costText(k.cost) });
  facts.push(...takesFacts(s, JobKind.JOB_KIND_SEA_OBJECT, k.timeSec));
  facts.push(fact(S.info_needs, S.info_so_depot({ level: k.depotLevel })));
  if (k.tech !== 0) facts.push(fact(S.info_needs, s.tech(k.tech)?.name ?? S.word_tech));
  // What the order does to the stock (R1081): the items of the kind in the
  // Depot now and after the one item the press orders, and the rack.
  const d = s.empire?.depot;
  const effect: EffectRow[] = [];
  if (d) {
    const held = depotHeld(d, k.id);
    effect.push({ key: S.info_so_held, now: groupThousands(held), after: groupThousands(held + 1), delta: '+1' });
    facts.push(fact(S.info_so_rack, S.info_bucket_of({ used: groupThousands(d.stocked), cap: groupThousands(d.rack) })));
  }
  const family = k.seaMine ? CS.so_mine : CS.so_deployable;
  return {
    size: 'card', mark: rowIcon(k.seaMine ? 'mine' : 'deployable'), title: k.name, ink: 'catalogue',
    kind: S.info_so_kind({ family, level: k.depotLevel }),
    status: pressChip(press.state),
    body: desc(S.seaobject_uses[k.id - 1] ?? '') + (k.seaMine ? desc(CS.so_note_mine) : '')
      + effectHtml(effect) + factsHtml(facts),
    note: pressNote(esc(why), press),
    actions: [pressAction(press)],
  };
}

// --- a module family ----------------------------------------------------------

/**
 * The module-family card (R701): what each of the three tiers is worth
 * before the roll, the gate it asks for, what it adds to the build price
 * and to the draft, then the roll range and the odds of each grade (R1194).
 * Every figure is the module's own. The card never says what the fitted
 * hull becomes: the Dampening inversion and its floor are server-side
 * (numbers.md §6) and neither is on the wire. A family has no verb press:
 * the draft form of DRAFT · INVENTION picks a tier.
 */
function moduleSpec(s: GameState, id: number): CardSpec | null {
  const mods = (s.catalog?.modules ?? []).filter((m) => m.family === id);
  if (mods.length === 0) return null;
  const facts: Fact[] = [];
  for (const m of mods) {
    facts.push(fact(S.info_module_tier({ tier: S.module_tiers[m.tier - 1] ?? m.tier }),
      S.info_module_worth({ pct: (m.effectPct / 10).toFixed(1) })));
    if (m.tech !== 0) {
      const node = s.tech(m.tech);
      // A gate this empire holds reads `done`, one it lacks reads `locked`.
      facts.push({
        key: S.info_needs,
        val: `<span class="${s.hasTech(m.tech) ? 'done' : 'locked'}">${esc(S.info_module_gate({
          code: node?.code ?? '?', tech: node?.name ?? S.word_a_doctrine,
        }))}</span>`,
      });
    }
    // A whole percentage sheds its decimal: 15% and not 15.0%.
    const share = m.costShare * 100;
    facts.push(fact('', S.info_module_build_share({ share: Number.isInteger(share) ? share : share.toFixed(1) })));
    facts.push(fact(S.info_module_draft, m.draftIsotopes > 0 ? S.info_module_isotopes({ n: m.draftIsotopes }) : S.info_none));
  }
  // The grades of the roll with their odds (R1194). The server computes
  // every chance; the card only states them.
  const grades = s.rules.moduleGrades.map((g) => {
    const odds = gradeOdds(g.chance);
    return fact(moduleGradeWord(g.code), S.info_grade_odds({
      low: g.minRoll, high: g.maxRoll, pct: odds.pct, onein: odds.oneIn,
    }));
  });
  return {
    size: 'card', mark: rowIcon('module'), title: mods[0].name, ink: 'catalogue',
    kindHtml: termsHtml(S.info_module_sub),
    body: desc(S.module_effects[id - 1] ?? '') + factsHtml(facts)
      // The roll rule is a fact of the DRAFT fold (R1895); the grade odds
      // below state what the wait gambles on.
      + factsHtml(grades),
  };
}

// --- a tech node (084 card 15) -------------------------------------------------

/** The shape of one step of the tech ladder: a check for done, a heavy ring
 *  for this node, a plain ring for a step still open on the path, and a
 *  dashed ring with a lock for a node that comes later. */
type LadderState = 'done' | 'this' | 'open' | 'later';

/** One step of the tech ladder. */
interface LadderStep {
  id: number;
  code: string;
  name: string;
  state: LadderState;
  /** The small words at the right: this node, the other branch of a step,
   *  or what else a later node needs. */
  note: string;
}

/** Tier first, then the code: the order of the shelf. */
const byTier = (a: TechSpec, b: TechSpec): number => a.tier - b.tier || a.code.localeCompare(b.code);

/**
 * The ladder of one node (084 card 15): every node it needs, near or far,
 * from the first one of the path; the node itself; and the nodes it leads
 * to. The catalog names each node and what it needs, so the ladder reads
 * those fields backwards and mirrors no tech table. A node with two needs
 * draws a fork as a note on the later step, not as a branch.
 */
export function techLadder(s: GameState, id: number): LadderStep[] {
  const all = s.catalog?.tech ?? [];
  const node = all.find((n) => n.id === id);
  if (!node) return [];
  const need = new Set<number>();
  const walk = (n: TechSpec) => {
    for (const r of n.requires) {
      if (need.has(r)) continue;
      need.add(r);
      const up = all.find((x) => x.id === r);
      if (up) walk(up);
    }
  };
  walk(node);
  const before = all.filter((n) => need.has(n.id)).sort(byTier);
  const after = all.filter((n) => n.requires.includes(id)).sort(byTier);
  const steps: LadderStep[] = before.map((n) => ({
    id: n.id, code: n.code, name: n.name, state: s.hasTech(n.id) ? 'done' : 'open',
    note: n.branch !== node.branch ? n.branch : '',
  }));
  steps.push({ id, code: node.code, name: node.name, state: s.hasTech(id) ? 'done' : 'this', note: CS.ladder_this });
  for (const n of after) {
    const other = n.requires.filter((r) => r !== id && !s.hasTech(r)).map((r) => s.tech(r)?.code ?? '?');
    steps.push({
      id: n.id, code: n.code, name: n.name, state: s.hasTech(n.id) ? 'done' : 'later',
      note: other.length > 0 ? CS.ladder_needs({ codes: other.join(', ') }) : '',
    });
  }
  return steps;
}

/** The ladder as markup: one 32 px row per step, the shape at the left. */
function ladderHtml(steps: readonly LadderStep[]): string {
  if (steps.length < 2) return '';
  return '<div class="ck-lad">' + steps.map((st) => `<div class="ck-lr ${st.state}">`
    + `<span class="ck-ld">${st.state === 'done' ? rowIcon('check') : st.state === 'later' ? rowIcon('lock') : ''}</span>`
    + `<span class="ck-lc">${esc(st.code)}</span><b>${esc(st.name)}</b>`
    + `<small>${esc(st.note)}</small></div>`).join('') + '</div>';
}

/**
 * What a doctrine opens (R1081): the installations, hulls, rounds and
 * module families it gates. Every name comes from the hello catalog: each
 * row carries the id of the node that gates it.
 */
function techOpens(s: GameState, id: number): Fact[] {
  const c = s.catalog;
  if (!c) return [];
  const join = (names: string[]) => names.filter((x) => x !== '').join(' · ');
  const rows: [string, string][] = [
    [S.info_opens_buildings, join(c.buildings.filter((x) => x.tech === id).map((x) => x.name))],
    [S.info_opens_hulls, join(c.ships.filter((x) => x.tech === id).map((x) => x.name))],
    [S.info_opens_rounds, join(c.torpedoes.filter((x) => x.tech === id).map((x) => x.name))],
    // A module family carries one row per tier, so the family name would
    // repeat once a tier without the set.
    [S.info_opens_modules, join([...new Set(c.modules.filter((x) => x.tech === id).map((x) => x.name))])],
  ];
  return rows.filter(([, v]) => v !== '').map(([k, v]) => fact(k, v));
}

function techSpec(s: GameState, id: number): CardSpec | null {
  const n = s.tech(id);
  if (!n) return null;
  const { press, note, gated } = techPress(s, n);
  const facts: Fact[] = [{ key: S.info_costs, val: costText(n.cost) }];
  facts.push(...takesFacts(s, JobKind.JOB_KIND_RESEARCH, n.timeSec));
  facts.push(fact(S.info_needs, S.info_research_lab_level({ level: n.labLevel })));
  return {
    size: 'card', mark: techIcon(n.code), title: n.name, ink: 'catalogue',
    kind: `${n.branch} · ${S.info_tech_sub({ code: n.code, tier: n.tier })}`,
    status: pressChip(press.state),
    body: desc(n.unlocks) + sect(CS.sect_path, ladderHtml(techLadder(s, id)))
      + sect(CS.sect_opens, factsHtml(techOpens(s, id))) + factsHtml(facts),
    note: pressNote(gated ? esc(note) : '', press),
    actions: [pressAction(press)],
  };
}

// --- a captain (084 card 16, R1118) -------------------------------------------

/**
 * What one captain does now, as the busy line, in markup that is already
 * escaped. `bare` drops the dock hint from the ashore state: a card in the
 * pens, a cadet and the captain card (R1118) must not send the reader to
 * the dock. `fleets` are the own fleets at sea, which name the fleet a
 * captain commands. `link` makes that name the button that selects the
 * fleet (R1483); the captain card passes false, because its SHOW press
 * does that (R1826).
 */
export function captainBusy(
  s: GameState, c: Captain, bare: boolean,
  fleets: readonly { id: number; name: string }[] = [], link = false,
): string {
  // A captain whose dive still waits in the queue (R737). The server
  // refuses every other order with it, so the branch comes before the
  // fleet one: a captain in the queue holds no hull. The job's count is
  // the depth, so the sentence names the dive.
  const prep = outfitJob(s, c.id);
  if (!c.capturedBy && c.injuredSecs === 0 && !c.expedition && !prep && c.fleetId) {
    return captainFleetHtml(s, c.fleetId, fleets, link);
  }
  return withResIcons(esc(c.capturedBy
    ? S.captain_prisoner({ who: c.capturedBy, ransom: c.ransom })
    : c.injuredSecs > 0
      ? S.captain_recovering({ left: dur(c.injuredSecs) })
      : c.expedition
        ? S.captain_expedition
        : prep
          ? S.captain_outfitting({ dive: diveName(s, prep.count) })
          // The hint names the dock. A card standing in the pens must not
          // send the reader where the reader already is.
          : bare ? S.captain_ashore : S.captain_ashore_hint));
}

/**
 * The fleet a captain commands, as markup (R1483). A fleet at sea is its
 * name, and with `link` a button that selects the fleet and centres the
 * map on it. A hull in the pens has no place on the map, so it is the plain
 * name. A fleet the client does not know yet falls back to its number.
 */
function captainFleetHtml(
  s: GameState, fleetId: number, fleets: readonly { id: number; name: string }[], link: boolean,
): string {
  const sea = fleets.find((f) => f.id === fleetId);
  if (sea) return link ? capFleetButton(sea) : esc(sea.name);
  const pen = s.pennedHull(fleetId);
  return esc(pen ? S.captain_in_pens({ name: pen.name }) : S.captain_commanding({ id: fleetId }));
}

/** The four ages of the career strip and where the captain stands on it. */
interface CareerStrip {
  /** The first age of the strip: the youngest age a cadet joins at. */
  from: number;
  peak: number;
  decline: number;
  /** The last age of the strip. */
  to: number;
  age: number;
}

/**
 * The career strip of one captain (084 card 16): rise, peak and decline,
 * from the age arc of the catalog rules (`CatalogRules.captain`, R1170;
 * 084 open point 8), never from a literal. The strip starts at the
 * youngest recruit age and runs past the decline age by the length of the
 * peak, or one season past the captain's age when the captain is older.
 * The geometry is the client's; every age is the server's.
 */
export function careerStrip(c: Captain, r: CaptainRules): CareerStrip {
  const from = Math.min(r.recruitAgeMin, c.age);
  const peak = Math.max(from, r.peakAtAge);
  const decline = Math.max(peak, r.declineAtAge);
  const to = Math.max(decline + Math.max(decline - peak, r.yearsPerSeason), c.age + r.yearsPerSeason);
  return { from, peak, decline, to, age: c.age };
}

/** The career strip as markup: the age and the phase over three segments,
 *  the mark at the age, and the three phase words under them. */
function careerHtml(c: Captain, r: CaptainRules): string {
  const k = careerStrip(c, r);
  const span = Math.max(1, k.to - k.from);
  const phase = phaseWord(c);
  const at = clamp01((k.age - k.from) / span) * 100;
  return '<div class="ck-car">'
    + `<div class="ck-cl"><span>${esc(CS.career_age({ age: c.age, phase }))}</span>`
    + `<span>${esc(CS.career_decline_from({ age: k.decline }))}</span></div>`
    + `<div class="ck-cs"><i class="rise" style="flex:${k.peak - k.from}"></i>`
    + `<i class="peak" style="flex:${k.decline - k.peak}"></i>`
    + `<i class="dec" style="flex:${k.to - k.decline}"></i>`
    + `<b style="left:${at.toFixed(1)}%"></b></div>`
    + `<div class="ck-cl"><span>${esc(CS.career_rise)}</span><span>${esc(CS.career_peak)}</span>`
    + `<span>${esc(CS.career_decline)}</span></div></div>`;
}

/**
 * The ability rows of the captain card (round 100 card 01 "The next pip",
 * R2087). Each attribute is a row: the catalog name (★ and the hero ink for
 * a talent, a shape beside the word), one pip for each point up to
 * `attr_train_cap`, and the practice percent of the next practice point,
 * or MAX at the attribute cap and FULL at the season cap. The next pip
 * fills from the left with that practice share; an Academy job stripes it
 * in the accent instead. Line 2 holds the season dots, which stand only
 * when the attribute earned a practice point this season (the owner's
 * change of the drawing), the clock of the Academy job, and the effect
 * the captain gives now. A cadet does not practise, so its rows hold no
 * percent and no dots.
 *
 * Under the rows stands the one TRAIN press (`trainPickGate`). It opens
 * the pick of the six attributes, each with the Academy time of its next
 * point from `Captain.train_secs`: the server computes the time, and the
 * client mirrors no curve (R1081). A chip reads `trainGate`, the gate of ⋯
 * of the captain row too. A press that cannot act names its one reason
 * beside it; a training job draws no press, because its clock stands on
 * its row; a cadet and a prisoner get one line instead.
 */
function abilityRowsHtml(s: GameState, c: Captain, r: CaptainRules, pickOpen: boolean): string {
  const job = c.cadet ? undefined : trainingJob(s, c);
  let html = `<div class="ck-pp${c.capturedBy ? ' held' : ''}">`;
  for (let i = 0; i < c.attrs.length; i++) {
    const f = attrTrainFigures(c, i, r);
    const name = attrName(s, i);
    const tal = c.talents.includes(i);
    const onJob = job?.count === i;
    const stop = practiceStopWord(f);
    let pips = '';
    for (let k = 0; k < f.cap; k++) {
      if (k < f.value) pips += '<i class="f"></i>';
      else if (k === f.value && onJob) pips += '<i class="a"></i>';
      else if (k === f.value && !c.cadet && !stop && f.pct > 0) pips += `<i class="p" style="--pf:${f.pct}%"></i>`;
      else pips += '<i></i>';
    }
    const label = esc(attrValueText(f, name));
    const pc = c.cadet ? '<span class="pp-pc"></span>'
      : `<span class="pp-pc${stop ? ' stop' : ''}">${esc(stop || S.captrain_pct({ pct: f.pct }))}</span>`;
    // The season dots stand once the attribute earned a practice point
    // this season (R2087), and never at the attribute cap.
    let dots = '';
    if (!c.cadet && !f.atCap && f.practised > 0) {
      const t = esc(S.captrain_dots({ n: f.practised, cap: f.seasonCap }));
      dots = `<span class="pp-sd" role="img" title="${t}" aria-label="${t}">`;
      for (let k = 0; k < f.seasonCap; k++) dots += k < f.practised ? '<i class="on"></i>' : '<i></i>';
      dots += '</span>';
    }
    const clk = job && onJob
      ? `<span class="pp-clk">${esc(job.active ? dur(s.jobEtaSec(job)) : CS.st_queued)}</span>` : '';
    const eff = c.effects ? esc(captainEffectText(i, c.effects)) : '';
    html += `<div class="pp-r${tal ? ' t' : ''}"><span class="pp-n">${esc(name)}`
      + (tal ? `<span class="pp-star">${esc(S.capcard_talent_mark)}</span>` : '') + '</span>'
      + `<span class="pp-pips" role="img" title="${label}" aria-label="${label}">${pips}</span>${pc}`
      + `<div class="pp-l2">${dots}${clk}<span class="pp-e" title="${eff}">${eff}</span></div></div>`;
  }
  return html + '</div>' + trainFootHtml(s, c, r, pickOpen);
}

/** The foot of the ability rows: the TRAIN press, its pick, its one
 *  reason, or the line of a cadet and of a prisoner (R2087). */
function trainFootHtml(s: GameState, c: Captain, r: CaptainRules, pickOpen: boolean): string {
  const g = trainPickGate(s, c);
  if (!g.show) return `<p class="pp-why">${esc(c.cadet ? S.capcard_why_cadet : S.capcard_why_held)}</p>`;
  if (g.on) return '';
  const press = S.captrain_pick_press;
  if (!g.can) {
    return `<div class="pp-foot"><button class="pp-sp" disabled>${esc(press)}</button>`
      + `<span class="pp-why">${esc(g.title)}</span></div>`;
  }
  if (!pickOpen) {
    return `<div class="pp-foot"><button class="pp-sp" data-act="trainpick" aria-expanded="false"`
      + ` title="${esc(g.title)}">${esc(press)}</button></div>`;
  }
  const chips = c.attrs.map((_, i) => {
    const f = attrTrainFigures(c, i, r);
    const short = ATTR_SHORT[i] ?? '';
    const sec = c.trainSecs[i] ?? 0;
    const tg = trainGate(s, c, i);
    const can = tg.can && sec > 0;
    const word = f.atCap ? S.captrain_pick_max({ short }) : S.captrain_pick_chip({ short, v: f.value, next: f.value + 1 });
    return `<button data-act="train" data-id="${c.id}" data-n="${i}" title="${esc(tg.title)}"${can ? '' : ' disabled'}>`
      + `${esc(word)}<small>${esc(sec > 0 ? dur(sec) : S.captrain_pick_none)}</small></button>`;
  });
  return `<button class="pp-ph" data-act="trainpick" aria-expanded="true" title="${esc(S.captrain_pick_close)}">`
    + `<span class="ck-cap">${esc(S.captrain_pick_head)}</span><span class="ck-cap on">${esc(S.captrain_pick_open)}</span></button>`
    + `<div class="pp-pick">${chips.join('')}</div>`;
}

/** The status chip of a captain: where the captain is, by the busy state. */
function captainChip(c: Captain, atSea: boolean): CardSpec['status'] {
  if (c.capturedBy) return { text: CS.st_prisoner, tone: 'bad' };
  if (c.injuredSecs > 0) return { text: CS.st_recovering, tone: 'amber' };
  if (c.cadet) return { text: CS.st_cadet, tone: 'kind' };
  return atSea ? { text: CS.st_at_sea, tone: 'on' } : undefined;
}

/**
 * One captain's card (R1118, numbers.md §12, 084 card 16, round 100 card
 * 01): the ability rows with the TRAIN press and its pick (R2087), the
 * career strip, the facts of this captain alone, the backstory folded to
 * one line with MORE, and the action row: SHOW <FLEET>, which selects that
 * one fleet and centres the map, then the presses of `captainActions`
 * (RELEASE, PAY, or RECRUIT and PASS of a cadet). The facts are Now, the
 * practice rate of a captain in decline, and the decline roll above 0%;
 * every rule that is the same for each captain stands in the HOW IT WORKS
 * fold of CAPTAIN (R2087). The server ships every figure (`Captain.effects`,
 * `Captain.train_secs`, `rules.captain`), so the card picks a sentence and
 * computes no bonus and no time.
 */
function captainSpec(s: GameState, id: number, ctx: InfoCtx): CardSpec | null {
  const c = s.captain(id);
  const r = s.rules.captain;
  if (!c || !r) return null;
  // The job's clock stands on its ability row (R2087).
  const facts: Fact[] = [{ key: S.capinfo_now, val: captainBusy(s, c, true, ctx.fleets) }];
  if (c.phaseKind === CaptainPhase.CAPTAIN_PHASE_DECLINE) facts.push(fact(S.capinfo_practice, practiceDeclineText(r)));
  // The decline roll the NEXT season roll makes after a season served. A
  // captain one season short of decline already runs it, so the fact
  // follows the shipped figure and not the phase, and stands above 0%.
  const risk = c.effects?.retireRiskPct ?? 0;
  if (risk > 0) facts.push(fact(S.capinfo_retire, S.capinfo_retire_val({ pct: pctNum(risk) })));
  // The owner's backstory (R1246): the meta server holds it and the wire
  // never carries it, so the card asks for it on the first open and draws
  // it when it lands. Folded, it is one line with MORE.
  askBackstories(ctx.onBackstory);
  const story = backstoryOf(c.id).replace(/\r\n?/g, '\n').trim();
  const storyHtml = story
    ? `<p class="ck-story${ctx.storyOpen ? ' open' : ''}" title="${esc(S.capinfo_backstory)}">`
      + `<span>${ctx.storyOpen ? esc(story).replace(/\n/g, '<br>') : esc(story.replace(/\n/g, ' '))}</span>`
      + `<button class="ck-sm" data-act="story">${ctx.storyOpen ? CS.story_less : CS.story_more}</button></p>`
    : '';
  const sea = !c.capturedBy && c.injuredSecs === 0 && !c.expedition && c.fleetId
    ? ctx.fleets.find((f) => f.id === c.fleetId) : undefined;
  const actions: Action[] = [
    ...(sea ? [{ act: 'capfleet', id: sea.id, face: 'dark' as const, label: CS.show_fleet({ name: sea.name.toUpperCase() }) }] : []),
    ...captainActions(s, c, ctx.armed ?? (() => false), { train: false }),
  ];
  return {
    size: 'card', mark: rowIcon('captain'), title: c.name, ink: 'catalogue',
    kindHtml: termsHtml(c.cadet ? S.capinfo_sub_cadet : S.capinfo_sub),
    status: captainChip(c, sea !== undefined),
    body: abilityRowsHtml(s, c, r, ctx.trainPick ?? false) + careerHtml(c, r) + factsHtml(facts) + storyHtml,
    actions,
  };
}

// --- the page of a telephone (087 card 02) ------------------------------------

/**
 * The back bar of the page in the menu sheet (084 card 13, 087 card 02):
 * the back chip with the list it came from, the place of the item in that
 * list, and the stepper ‹ ›, which costs no row of its own. A list of one
 * item draws no stepper.
 */
export function backBarHtml(from: string, pos: number, count: number, act = 'infoback'): string {
  const step = count > 1
    ? `<span class="ib-pos">${esc(CS.step_of({ n: pos + 1, m: count }))}</span>`
      + `<button class="ib-st" data-act="infostep" data-n="-1" title="${esc(CS.step_prev_title)}"`
      + ` aria-label="${esc(CS.step_prev_title)}">${esc(CS.step_prev)}</button>`
      + `<button class="ib-st" data-act="infostep" data-n="1" title="${esc(CS.step_next_title)}"`
      + ` aria-label="${esc(CS.step_next_title)}">${esc(CS.step_next)}</button>`
    : '';
  return `<div class="ibar"><button class="ib-back" data-act="${act}" title="${esc(CS.back_title)}">`
    + `<i>${esc(CS.back_mark)}</i>${esc(from)}</button>${step}</div>`;
}

/** The item `dir` steps from `cur` in `list`, round the ends; null when
 *  `cur` is not in the list or the list holds one item. */
export function stepTarget(list: readonly InfoTarget[], cur: InfoTarget, dir: number): InfoTarget | null {
  const i = list.findIndex((t) => t.kind === cur.kind && t.id === cur.id);
  if (i < 0 || list.length < 2) return null;
  return list[(i + dir + list.length) % list.length] ?? null;
}
